/* eslint-disable no-restricted-syntax */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import APIGateway from '@/api';
import { pollModelReady } from '@/utils/pollModelReady';

const modelsKeys = {
  all: [ 'models' ] as const,
  list: [ 'models', 'list' ] as const,
  info: ( modelId: string ) => [ ...[ 'models' ] as const, 'info', modelId ] as const
};

export function useModelsQuery( opts: { enabled: boolean } ) {
  return useQuery( {
    queryKey: modelsKeys.list,
    queryFn: async() => {
      const models = await APIGateway.models.modelsGet();
      return models.models;
    },
    enabled: opts.enabled
  } );
}

export function useModelInfoQuery( modelId: string | undefined, opts: { enabled: boolean } ) {
  return useQuery( {
    queryKey: modelsKeys.info( modelId ?? 'undefined' ),
    queryFn: async() => {
      if ( !modelId ) {
        throw new Error( 'Model ID is required' );
      }
      await pollModelReady( modelId );
      return await APIGateway.models.modelGetInfo( { litellm_model_id: modelId } );
    },
    enabled: opts.enabled && !!modelId,
    retry: false
  } );
}

export function useCreateModel() {
  const queryClient = useQueryClient();

  return useMutation( {
    mutationFn: async( params: {
      model_name: string;
      litellm_params: { model: string; [key: string]: unknown };
      model_info: { id?: string; mode: string; description?: string };
    } ) => {
      return await APIGateway.models.modelCreate( params );
    },
    onSuccess: () => {
      queryClient.invalidateQueries( { queryKey: modelsKeys.all } );
    }
  } );
}

export function useUpdateModel() {
  const queryClient = useQueryClient();

  return useMutation( {
    mutationFn: async( params: {
      model_name: string;
      litellm_params: { model: string; [key: string]: unknown };
      model_info: { id: string; mode: string; description?: string };
    } ) => {
      return await APIGateway.models.modelUpdate( params );
    },
    onSuccess: () => {
      queryClient.invalidateQueries( { queryKey: modelsKeys.all } );
    }
  } );
}

export function useDeleteModel() {
  const queryClient = useQueryClient();

  return useMutation( {
    mutationFn: async( id: string ) => {
      return await APIGateway.models.modelDelete( { id } );
    },
    onSuccess: () => {
      queryClient.invalidateQueries( { queryKey: modelsKeys.all } );
    }
  } );
}